import React, { useState, useContext, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';

import { useQuery, ApolloError } from '@apollo/client';
import { GraphQLErrors } from '@apollo/client/errors';
import { useAuth0 } from '@auth0/auth0-react';

import routes from 'lane-shared/config/routes';
import { AppContext } from 'lane-shared/contexts';
import { getChannel } from 'lane-shared/graphql/query';
import getAuth0Parameter from 'lane-shared/helpers/auth0/getAuth0Parameter';
import { imageUrl } from 'lane-shared/helpers/formatters';
import {
  InviteOnlyError,
  useIdentifyUser,
} from 'lane-shared/hooks/auth/useIdentifyUser';

import { ErrorMessage, Loading } from 'components/general';
import { UserIdentificationAndOnboardingTemplate as Template } from 'components/pageTemplate/UserIdentificationAndOnboarding';

import { EmailForm } from './EmailForm';
import { useInviteContext } from 'lane-shared/contexts/InviteContext';
import { useHideAuth0LoginAlertBox } from 'lane-shared/hooks/auth0/useHideAuth0LoginAlertBox';
import { useAuth0Enabled } from 'lane-shared/hooks/auth0/useAuth0Enabled';
import { validateByEmailDomainFilter } from 'lane-shared/helpers/user/validateByEmailDomainFilter';
import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';
import { DefaultLocale } from 'localization';

const TRANSLATION_KEYS = {
  corruptedInviteEmail:
    'shared.identifyUser.loginEmail.error.corruptedInviteEmail',
  inviteOnlyError: 'shared.identifyUser.loginEmail.error.inviteOnly',
  emailDomainFilterError:
    'shared.identifyUser.loginEmail.error.emailDomainFilter',
};

const INVITE_ID_QUERY_PARAM = 'inviteId';
const CONTENT_PATH_QUERY_PARAM = 'contentPath';
const CHANNEL_ID_QUERY_PARAM = 'chid';

export const IdentifyUser = () => {
  const { whitelabel } = useContext(AppContext);

  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<
    ApolloError | GraphQLErrors | Error | undefined
  >(undefined);

  const { t } = useTranslation();
  const { loginWithRedirect, isAuthenticated, error: auth0Error } = useAuth0();
  const { identifyUser } = useIdentifyUser();
  const { search } = useLocation();
  const hideAuth0LoginAlertBox = useHideAuth0LoginAlertBox();
  const isAuth0Enabled = useAuth0Enabled();

  const searchParams = new URLSearchParams(search);
  const searchParamInviteId = searchParams.get(INVITE_ID_QUERY_PARAM);
  const searchParamContentPath = searchParams.get(CONTENT_PATH_QUERY_PARAM);
  const searchParamChannelId = searchParams.get(CHANNEL_ID_QUERY_PARAM);

  const {
    setInviteId,
    email: inviteEmail,
    error: inviteError,
    building,
    company,
  } = useInviteContext();

  const { data: publicProfileChannel, loading: publicProfileChannelLoading } =
    useQuery(getChannel, {
      variables: { id: searchParamChannelId },
      skip: !searchParamChannelId,
    });

  const invitedChannel = building || company || publicProfileChannel?.channel;

  if (searchParamInviteId && setInviteId) {
    setInviteId(searchParamInviteId);
  }

  const fetchAndRedirectToAuth0 = async (email: string, selectedLocale?: string) => {
    const {
      loginEmailExists,
      invitation: auth0Invitation,
      connection,
      locale: userLocale,
    } = await identifyUser(email);

    if (!whitelabel.auth0OrganizationId && auth0Invitation?.organizationId) {
      whitelabel.auth0OrganizationId = auth0Invitation.organizationId;
    }

    const isInvitedByActivateAdmin = Boolean(inviteEmail);

    if (isInvitedByActivateAdmin && email !== inviteEmail) {
      setError(new Error(t(TRANSLATION_KEYS.corruptedInviteEmail)));

      return;
    }

    // Determine locale priority: selected > user's stored > whitelabel > default
    let finalLocale = selectedLocale;

    if (!finalLocale && loginEmailExists) {
      finalLocale = userLocale;
    }

    if (!finalLocale) {
      finalLocale = whitelabel.locale ?? DefaultLocale;
    }
    
    const auth0Params = getAuth0Parameter(
      whitelabel,
      email,
      loginEmailExists,
      auth0Invitation?.ticketId,
      searchParamInviteId || '',
      connection,
      hideAuth0LoginAlertBox,
      finalLocale
    );
    
    return loginWithRedirect({
      authorizationParams: auth0Params,
      appState: {
        inviteId: searchParamInviteId,
        contentPath: searchParamContentPath,
        channelId: searchParamChannelId,
      },
    });
  };

  const onSubmit = useCallback(
    (email: string, selectedLocale?: string) => {
      setIsLoading(true);
      setError(undefined);

      if (isWorkplaceOnboardingEnabled) {
        const emailDomain = email?.split('@')[1]?.toLowerCase() ?? '';
        const isEmailValid = validateByEmailDomainFilter(
          emailDomain,
          publicProfileChannel?.channel?.filters ?? []
        );

        if (!isEmailValid) {
          setError(
            new Error(
              t(TRANSLATION_KEYS.emailDomainFilterError, {
                emailDomain,
              })
            )
          );
          setIsLoading(false);

          return;
        }
      }

      fetchAndRedirectToAuth0(email, selectedLocale)
        .catch(err => {
          if (err instanceof InviteOnlyError) {
            setError(new Error(t(TRANSLATION_KEYS.inviteOnlyError)));
          } else {
            setError(err);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [fetchAndRedirectToAuth0, t]
  );

  if (isAuthenticated) {
    return <Redirect to={routes.home} />;
  }

  if (!isAuth0Enabled) {
    return <Redirect to={routes.login} />;
  }

  if (
    (searchParamInviteId && !inviteEmail && !inviteError) ||
    (searchParamChannelId && publicProfileChannelLoading)
  ) {
    return <Loading />;
  }

  return (
    <Template
      logo={imageUrl(whitelabel.profile.logo)}
      name={whitelabel.profile.name}
    >
      <EmailForm
        isLoading={isLoading}
        email={inviteEmail}
        onSubmit={onSubmit}
        invitedChannel={invitedChannel}
      />
      {Boolean(error) && <ErrorMessage error={error} />}
      {Boolean(inviteError) && <ErrorMessage error={inviteError} />}
      {Boolean(auth0Error) && <ErrorMessage error={auth0Error} />}
    </Template>
  );
};
